import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  X,
  Upload,
  Check,
  AlertCircle,
  FileText,
  Building,
  Briefcase,
  User,
  CheckCircle,
} from "lucide-react";
import { buildApiUrl } from '../../utils/apiSetup';
import { useNotification } from '../../contexts/NotificationContext';

const ManageDocumentsModal = ({ isOpen, onClose, employee }) => {
  const [documentStatus, setDocumentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useNotification();
  const [uploading, setUploading] = useState(null);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  useEffect(() => {
    if (isOpen && employee) {
      fetchDocumentStatus();
    }
  }, [isOpen, employee]);

  const fetchDocumentStatus = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(buildApiUrl(`/api/v1/employee-master/${employee.id}/document-status`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setDocumentStatus(data);
      } else {
        setError("Failed to fetch document status");
      }
    } catch (error) {
      setError("Network error while fetching document status");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (documentTypeId, documentName) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf,image/jpeg,image/png";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploading(documentTypeId);
      setError("");
      setSuccess("");

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(buildApiUrl(`/api/v1/employee-master/${employee.id}/documents?document_type_id=${documentTypeId}`),
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
            body: formData,
          },
        );

        if (response.ok) {
          setSuccess(`${documentName} uploaded successfully`);
          await fetchDocumentStatus(); // Refresh status
          setTimeout(() => setSuccess(""), 3000);
        } else {
          const data = await response.json().catch(() => ({}));
          setError(data.detail || `Failed to upload ${documentName}`);
        }
      } catch (error) {
        setError(`Network error while uploading ${documentName}`);
      } finally {
        setUploading(null);
      }
    };
    input.click();
  };

  const handleClose = () => {
    setDocumentStatus(null);
    setError("");
    setSuccess("");
    onClose();
  };

  if (!isOpen || !employee) return null;

  const isComplete = documentStatus?.complete;
  const totalCount = documentStatus?.total || 0;
  const uploadedCount = documentStatus?.uploaded || 0;
  const mandatoryRequired = documentStatus?.mandatory_required || 0;
  const mandatoryUploaded = documentStatus?.mandatory_uploaded || 0;
  const mandatoryMissing = documentStatus?.mandatory_missing || 0;
  const missingCount = documentStatus?.missing || 0;
  const completionPercentage = totalCount > 0 ? Math.round((uploadedCount / totalCount) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Manage Employee Documents
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Upload registration documents for this employee.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Employee Information */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <User className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <p className="text-xs text-gray-500">Employee Name</p>
                <p className="text-sm font-medium text-gray-900">
                  {employee.full_name || employee.emp_name}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <User className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <p className="text-xs text-gray-500">Employee Code</p>
                <p className="text-sm font-medium text-gray-900">
                  {employee.emp_code}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Building className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <p className="text-xs text-gray-500">Department</p>
                <p className="text-sm font-medium text-gray-900">
                  {employee.department || employee.department_name || "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Briefcase className="w-4 h-4 text-gray-500 mr-2" />
              <div>
                <p className="text-xs text-gray-500">Designation</p>
                <p className="text-sm font-medium text-gray-900">
                  {employee.emp_desig || employee.designation || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : documentStatus ? (
            <>
              {/* Progress Card */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Documents Progress
                </h4>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-lg font-bold text-gray-900">
                    {uploadedCount} / {totalCount} Uploaded
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {completionPercentage}%
                  </p>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-gray-600">
                    {missingCount} Document{missingCount !== 1 ? "s" : ""} Remaining
                  </p>
                  <p className="text-sm font-semibold text-gray-700">
                    Required: {mandatoryUploaded} / {mandatoryRequired} completed
                  </p>
                </div>
                {mandatoryMissing > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {mandatoryMissing} required document{mandatoryMissing !== 1 ? "s" : ""} remaining
                  </p>
                )}
              </div>

              {/* Success Banner */}
              {isComplete && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-green-800 mb-1">
                        All mandatory documents have been uploaded.
                      </h4>
                      <p className="text-sm text-green-700">
                        This employee is ready for credential generation.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Registration Documents Checklist */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Registration Documents
                </h4>
                <div className="space-y-3">
                  {documentStatus.documents.map((doc) => (
                    <div
                      key={doc.document_type_id}
                      className={`p-4 border rounded-lg ${
                        doc.uploaded
                          ? "bg-green-50 border-green-200"
                          : "bg-amber-50 border-amber-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          {doc.uploaded ? (
                            <Check className="w-5 h-5 text-green-600 mr-3" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-600 mr-3" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {doc.name}
                              {doc.is_mandatory && (
                                <span className="text-red-600 ml-1">*</span>
                              )}
                            </p>
                            <p
                              className={`text-xs ${doc.uploaded ? "text-green-600" : "text-amber-600"}`}
                            >
                              {doc.uploaded 
                                ? `Uploaded: ${doc.file_name || "Document"}` 
                                : (doc.is_mandatory ? "Required" : "Optional")}
                            </p>
                          </div>
                        </div>
                        {!doc.uploaded && (
                          <button
                            onClick={() =>
                              handleUpload(
                                doc.document_type_id,
                                doc.name,
                              )
                            }
                            disabled={uploading === doc.document_type_id}
                            className="ml-4 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          >
                            {uploading === doc.document_type_id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Upload
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  * Required document
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No document status available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ManageDocumentsModal;
